import { useEffect } from "react";
import { useUpdateExpeditionBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm, useWatch } from "react-hook-form";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { parseEnum } from "@/lib/enums.util";
import { DeliveryStatusFormData, DeliveryStatusFormSchema } from "./deliveryStatus.form";
import {
  useDeliveryStatusOptions,
  useExpeditionBlockedReasonOptions,
} from "@/hooks/useEnumOptions";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
};

const getDefaults = (expedition: Props["expedition"]): DeliveryStatusFormData => ({
  deliveryStatus:
    parseEnum(DeliveryStatusEnum, expedition.deliveryStatus) ?? DeliveryStatusEnum.WAITING,
  blockedReason: parseEnum(ExpeditionBlockedReasonEnum, expedition.blockedReason),
});

export const DeliveryStatusModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const deliveryStatusOptions = useDeliveryStatusOptions();
  const blockedReasonOptions = useExpeditionBlockedReasonOptions();
  const form = useForm<DeliveryStatusFormData>({
    defaultValues: getDefaults(expedition),
    resolver: zodResolver(DeliveryStatusFormSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const { mutateAsync: updateExpeditionBo, isPending } = useUpdateExpeditionBo();

  const deliveryStatus = useWatch({ control: form.control, name: "deliveryStatus" });

  useEffect(() => {
    if (open) form.reset(getDefaults(expedition));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expedition.id]);

  useEffect(() => {
    if (deliveryStatus !== DeliveryStatusEnum.FAILED && form.getValues("blockedReason")) {
      form.setValue("blockedReason", undefined, { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryStatus]);

  const onSubmit: SubmitHandler<DeliveryStatusFormData> = async (data) => {
    try {
      await updateExpeditionBo({
        id: expedition.id,
        payload: {
          deliveryStatus: data.deliveryStatus,
          ...(data.deliveryStatus === DeliveryStatusEnum.FAILED && {
            blockedReason: data.blockedReason,
          }),
        },
      });
      onClose();
    } catch {
      console.error("Error updating delivery status");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t("deliveryStatusModal.title")}
      description={t("deliveryStatusModal.description")}
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
            className="flex-1"
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            type="submit"
            form="deliveryStatusModalForm"
            disabled={isPending}
            className="flex-1"
          >
            {isPending ? (
              <Loader2 className="animate-spin" aria-label={t("deliveryStatusModal.saving")} />
            ) : (
              t("deliveryStatusModal.submit")
            )}
          </Button>
        </div>
      }
    >
      <form
        id="deliveryStatusModalForm"
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <ControlledSelectV2
          control={form.control}
          label={t("deliveryStatusModal.statusLabel")}
          name="deliveryStatus"
          options={deliveryStatusOptions}
        />
        {deliveryStatus === DeliveryStatusEnum.FAILED && (
          <ControlledSelectV2
            control={form.control}
            label={t("deliveryStatusModal.reasonLabel")}
            name="blockedReason"
            options={blockedReasonOptions}
          />
        )}
      </form>
    </Modal>
  );
};
